import React, { useEffect, useState } from "react";
import { Label } from "theme-ui";

/**
 * ColorPickerField — small color input for hex colors.
 *
 * Renders a native `<input type="color">` swatch alongside a text input
 * so the user can either pick from the OS picker or paste a hex string
 * directly. A "Reset" link clears the value to null so the client-side
 * palette rotation takes over again.
 *
 * Introduced in Session 30 (follow-up) for the Service Area create/edit
 * modals. Kept generic so other modules can reuse it later.
 */

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

interface ColorPickerFieldProps {
  label?: string;
  value?: string | null;
  onChange: (val: string | null) => void;
  helperText?: string;
  disabled?: boolean;
  /**
   * Session 30 follow-up: color to show in the swatch when `value` is
   * null/empty. Represents the auto-assigned palette color that will
   * render if the user leaves this blank. When set, the helperText is
   * suppressed in favor of "Using automatic color." so the null-value
   * state visually matches what the surrounding UI is already showing.
   */
  fallbackValue?: string | null;
}

export default function ColorPickerField({
  label = "Color",
  value,
  onChange,
  helperText,
  disabled,
  fallbackValue,
}: ColorPickerFieldProps) {
  // Local text state so the user can type freely (including intermediate
  // invalid states like "#12") without the parent form churning.
  const [text, setText] = useState<string>(value || "");
  const [invalid, setInvalid] = useState<boolean>(false);

  useEffect(() => {
    setText(value || "");
    setInvalid(false);
  }, [value]);

  // The native color input always needs a valid 7-char hex to render.
  // Prefer the saved value, then a caller-provided fallback (the auto
  // palette color), then neutral grey as a last resort.
  const swatchValue =
    value && HEX_RE.test(value)
      ? value
      : fallbackValue && HEX_RE.test(fallbackValue)
        ? fallbackValue
        : "#818181";

  const usingFallback = (!value || !HEX_RE.test(value)) && !!fallbackValue && HEX_RE.test(fallbackValue);

  const commitText = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      setInvalid(false);
      onChange(null);
      return;
    }
    // Accept "abcdef" without the leading # as a convenience.
    const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
    if (HEX_RE.test(withHash)) {
      setInvalid(false);
      setText(withHash.toLowerCase());
      onChange(withHash.toLowerCase());
    } else {
      setInvalid(true);
    }
  };

  return (
    <div style={{ marginBottom: 12 }}>
      {label && (
        <Label style={{ display: "block", marginBottom: 4 }}>{label}</Label>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="color"
          value={swatchValue}
          onChange={(e) => {
            const v = e.target.value.toLowerCase();
            setText(v);
            setInvalid(false);
            onChange(v);
          }}
          disabled={disabled}
          style={{
            width: 36,
            height: 32,
            padding: 0,
            border: "1px solid #ccc",
            borderRadius: 4,
            cursor: disabled ? "not-allowed" : "pointer",
            background: "transparent",
          }}
          aria-label={label ? `${label} swatch` : "Color swatch"}
        />
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commitText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitText((e.target as HTMLInputElement).value);
            }
          }}
          disabled={disabled}
          placeholder={
            fallbackValue && HEX_RE.test(fallbackValue)
              ? fallbackValue.toLowerCase()
              : "#4285f4"
          }
          spellCheck={false}
          style={{
            fontFamily: "monospace",
            fontSize: 13,
            padding: "6px 8px",
            border: `1px solid ${invalid ? "#A60000" : "#ccc"}`,
            borderRadius: 4,
            width: 110,
            textTransform: "lowercase",
          }}
          aria-invalid={invalid}
        />
        <button
          type="button"
          onClick={() => {
            setText("");
            setInvalid(false);
            onChange(null);
          }}
          disabled={disabled || !value}
          style={{
            background: "transparent",
            border: "none",
            color: disabled || !value ? "#c8c8c8" : "#818181",
            fontSize: 12,
            fontFamily: "Poppins, sans-serif",
            cursor: disabled || !value ? "default" : "pointer",
            textDecoration: "underline",
            padding: 0,
          }}
        >
          Reset to default
        </button>
      </div>
      {invalid && (
        <div style={{ color: "#A60000", fontSize: 11, marginTop: 4 }}>
          Enter a 6-digit hex like #4285f4.
        </div>
      )}
      {!invalid && usingFallback && (
        <div style={{ color: "#818181", fontSize: 11, marginTop: 4 }}>
          Using automatic color. Pick a hex to override.
        </div>
      )}
      {!invalid && !usingFallback && helperText && (
        <div style={{ color: "#818181", fontSize: 11, marginTop: 4 }}>{helperText}</div>
      )}
    </div>
  );
}
